<template>
	<vx-card
		no-shadow
		card-border
		style="height: 100%"
		:title="$t('VisitPoint') + ': ' + visitPointDetails.identification"
		:subtitle="$t('Vehicles.AssignedVehicle') + ': ' + visitPointDetails.vehicleId"
	>
		<template slot="actions">
			<feather-icon
				:icon="'XIcon'"
				svgClasses="w-5 h-5 m-1"
				class="cursor-pointer bg-primary text-white rounded-full font-bold"
				@click="$emit('closeSidebar')"
			/>
		</template>
		<vue-perfect-scrollbar class="scroll-area" :settings="settings">
			<vs-collapse id="detailVisitPoint" accordion type="border">
				<vs-collapse-item v-if="hasAdditionalData" icon-arrow="arrow_drop_down" :open="isOpenAdditionalData">
					<div slot="header" class="custom-header">
						{{ $t("AdditionalFields") }}
					</div>
					<vx-card no-shadow card-border>
						<table
							v-if="
								visitPointDetails.additional_data &&
								Object.keys(visitPointDetails.additional_data).length > 0
							"
						>
							<caption></caption>
							<thead>
								<tr>
									<th scope="col"></th>
								</tr>
							</thead>
							<tr v-for="(value, label) in visitPointDetails['additional_data']" :key="label">
								<td class="font-semibold">{{ label | capitalize }}:</td>
								<td>{{ value }}</td>
							</tr>
						</table>
					</vx-card>
				</vs-collapse-item>
				<vs-collapse-item v-if="hasDemand" icon-arrow="arrow_drop_down" :open="isOpenDemand">
					<div slot="header" class="custom-header">
						{{ $t("VisitPoints.Demands") }}
					</div>
					<vs-table :data="visitPointDetails.demand" pagination :max-items="itemsPerPage">
						<template slot="thead">
							<vs-th>{{ $t("Routing.Table.Name") }}</vs-th>
							<vs-th>{{ $t("ResourceModule.Create.CapacityValue") }}</vs-th>
						</template>
						<template slot-scope="{data}">
							<vs-tr :key="indextr" v-for="(tr, indextr) in data">
								<vs-td :data="tr.name">
									<div class="flex items-center">
										<span>{{ tr.name }}</span>
									</div>
								</vs-td>
								<vs-td :data="tr.amount">{{ tr.amount }}</vs-td>
							</vs-tr>
						</template>
					</vs-table>
				</vs-collapse-item>
				<vs-collapse-item v-if="hasSkills" icon-arrow="arrow_drop_down" :open="isOpenSkill">
					<div slot="header" class="custom-header">
						{{ $t("Skills.Skills") }}
					</div>
					<vs-table :data="visitPointDetails.skills" noDataText="" pagination :max-items="itemsPerPage">
						<template slot="thead">
							<vs-th>{{ $t("Name") }}</vs-th>
						</template>
						<template slot-scope="{data}">
							<vs-tr :key="indextr" v-for="(tr, indextr) in data">
								<vs-td :data="tr">{{ tr }}</vs-td>
							</vs-tr>
						</template>
					</vs-table>
				</vs-collapse-item>
				<vs-collapse-item v-if="hasAllowedVehicles" icon-arrow="arrow_drop_down" :open="isOpenAllowedVehicles">
					<div slot="header" class="custom-header">
						{{ $t("Vehicles.PermittedVehicles") }}
					</div>
					<vs-table
						:data="visitPointDetails.allowed_vehicles"
						noDataText=""
						pagination
						:max-items="itemsPerPage"
					>
						<template slot="thead">
							<vs-th>{{ $t("Identification") }}</vs-th>
						</template>
						<template slot-scope="{data}">
							<vs-tr :key="indextr" v-for="(tr, indextr) in data">
								<vs-td :data="tr">{{ tr }}</vs-td>
							</vs-tr>
						</template>
					</vs-table>
				</vs-collapse-item>
				<vs-collapse-item v-if="hasTimeWindows" icon-arrow="arrow_drop_down" :open="isOpenTimeWindows">
					<div slot="header" class="custom-header">
						{{ $t("TimeWindow.TimeWindows") }}
					</div>
					<vs-table :data="visitPointDetails.time_windows" noDataText="" pagination :max-items="itemsPerPage">
						<template slot="thead">
							<vs-th>{{ $t("CompanyCreate.InitDate") }}</vs-th>
							<vs-th>{{ $t("CompanyCreate.EndDate") }}</vs-th>
						</template>
						<template slot-scope="{data}">
							<vs-tr :key="indextr" v-for="(tr, indextr) in data">
								<vs-td :data="tr.init_time_window">{{ tr.init_time_window }}</vs-td>
								<vs-td :data="tr.end_time_window">{{ tr.end_time_window }}</vs-td>
							</vs-tr>
						</template>
					</vs-table>
				</vs-collapse-item>
			</vs-collapse>
		</vue-perfect-scrollbar>
	</vx-card>
</template>
<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
export default {
	name: "VisitingPointDetails",
	components: {
		VuePerfectScrollbar,
	},
	props: {
		visitPointDetails: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			itemsPerPage: 12,
			settings: {
				maxScrollbarLength: 60,
				wheelSpeed: 0.6,
			},
		};
	},
	computed: {
		hasAdditionalData() {
			return this.hasPropertyWithLength("additional_data");
		},
		hasDemand() {
			return this.hasPropertyWithLength("demand");
		},
		hasSkills() {
			return this.hasPropertyWithLength("skills");
		},
		hasAllowedVehicles() {
			return this.hasPropertyWithLength("allowed_vehicles");
		},
		hasTimeWindows() {
			return this.hasPropertyWithLength("time_windows");
		},
		isOpenAdditionalData() {
			return this.hasAdditionalData;
		},
		isOpenDemand() {
			return !this.hasAdditionalData && this.hasDemand;
		},
		isOpenSkill() {
			return !this.hasAdditionalData && !this.hasDemand && this.hasSkills;
		},
		isOpenAllowedVehicles() {
			return !this.hasAdditionalData && !this.hasDemand && !this.hasSkills && this.hasAllowedVehicles;
		},
		isOpenTimeWindows() {
			return (
				!this.hasAdditionalData &&
				!this.hasDemand &&
				!this.hasSkills &&
				!this.hasAllowedVehicles &&
				this.hasTimeWindows
			);
		},
	},
	methods: {
		hasPropertyWithLength(propertyName) {
			const prop = this.visitPointDetails[propertyName];
			return prop && Array.isArray(prop) ? prop.length > 0 : Object.entries(prop || {}).length > 0;
		},
	},
};
</script>
<style lang="scss" scoped>
.scroll-area {
	margin: auto;
	width: 100%;
	min-height: auto;
	max-height: calc(95vh - 150px);
}
.custom-header {
	font-size: 14px;
	font-weight: bold;
	color: #3f3b3b;
	border-radius: 5px;
}
</style>
<style lang="scss">
#detailVisitPoint {
	.vs-collapse-item--header {
		background-color: #f0f0f0;
	}
	.vs-icon {
		font-size: 1.7rem;
	}
}
</style>
